import { router } from '@inertiajs/vue3';
import { isMarketingPage } from '@/config/constants/seo';

/**
 * GA4 page views for Inertia navigations, and the tag's page scope.
 *
 * The gtag snippet (resources/views/seo/google-analytics.blade.php) sends the
 * first page view when the document loads. After that Inertia swaps components
 * over pushState without a reload, so nothing else would ever be recorded.
 *
 * Every navigation is checked against the marketing page list rather than
 * assumed: a visitor who lands on /pricing and then opens /login is still
 * carrying the loaded tag, and the app is not what this property measures.
 * Skipping the page view is not enough on its own — gtag keeps sending its
 * automatic events (engagement, scroll, form interactions, outbound clicks)
 * with the current URL — so the tag is also switched off entirely while a
 * non-marketing page is showing, through Google's `ga-disable-<id>` flag,
 * which gtag checks on every hit.
 *
 * Only ever active when the tag was emitted in the first place, which the
 * Blade guard already restricts to public marketing pages of the central
 * domain — so this never runs on a tenant subdomain or a token-scoped route.
 */
type GtagFn = (command: string, ...args: unknown[]) => void;

/**
 * How long to wait before reading `document.title` for a page view. Inertia
 * commits `<Head>` changes on a debounced timer (1 ms) that runs AFTER the
 * `navigate` event, so reading the title inside the listener records the
 * previous page's title against the new page's URL.
 */
export const PAGE_TITLE_SETTLE_MS = 50;

let registered = false;

const resolveGtag = (): GtagFn | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    return typeof window.gtag === 'function' ? window.gtag : null;
};

/**
 * Switch the tag off while a non-marketing page is showing, and back on when
 * the visitor returns to one.
 */
export const scopeTagToPage = (component: string | null | undefined): void => {
    if (typeof window === 'undefined' || !window.gaMeasurementId) {
        return;
    }

    window[`ga-disable-${window.gaMeasurementId}`] = !isMarketingPage(component);
};

/**
 * Send one `page_view` for a resolved Inertia page, if it is a marketing page
 * and the tag is present. The URL is fixed now; the title is read once
 * Inertia has committed the new page's `<Head>`. Exported for the test;
 * callers use `registerGoogleAnalyticsPageViews()`.
 */
export const sendMarketingPageView = (component: string | null | undefined, url?: string): boolean => {
    if (!isMarketingPage(component)) {
        return false;
    }

    const gtag = resolveGtag();

    if (gtag === null) {
        return false;
    }

    const pageLocation = url === undefined ? undefined : new URL(url, window.location.origin).href;

    window.setTimeout(() => {
        gtag('event', 'page_view', {
            page_location: pageLocation,
            page_title: document.title,
        });
    }, PAGE_TITLE_SETTLE_MS);

    return true;
};

/**
 * Attach the navigation listeners. Idempotent — a second call is a no-op, the
 * way `registerNavigationListener()` guards the PostHog equivalent.
 *
 * Must run before the app mounts. Inertia fires `navigate` for the page the
 * document loaded with, and the snippet's `gtag('config')` has already sent
 * that page view, so the first event is skipped. It is skipped whatever its
 * type: a back/forward arrival that missed the bfcache also reloads the
 * document, and reports a `history` navigate rather than an `initial` one.
 */
export const registerGoogleAnalyticsPageViews = (): void => {
    if (registered || resolveGtag() === null) {
        return;
    }

    registered = true;

    let documentPageSeen = false;

    // Before the new page is swapped in and its URL pushed, so no automatic
    // event can fire against a non-marketing URL in between.
    router.on('beforeUpdate', (event) => {
        scopeTagToPage(event.detail.page.component);
    });

    router.on('navigate', (event) => {
        const page = event.detail.page;

        // Back/forward restores the page from history without `beforeUpdate`.
        scopeTagToPage(page.component);

        if (!documentPageSeen) {
            documentPageSeen = true;

            return;
        }

        sendMarketingPageView(page.component, page.url);
    });
};
